/**
 * Configurações › Atendimento — o que vale para todos os atendimentos (spec 157).
 *
 * Cada atendimento tem o próprio documento, no passo Atendimento do agente; aqui
 * fica o que eles dividem: a lista de atendimentos, o orçamento, os conectores, os
 * operadores, a retenção e o registro de atendimento (FR-003, FR-012).
 *
 * **PUBLICAR CONFERE CADA ATENDIMENTO** (FR-004): a resposta traz `checked` por
 * atendimento; a recusa (409) nomeia atendimento e campo, e a tela mostra isso em
 * vez de um "algo deu errado". Quem não pode editar vê em leitura, com o motivo.
 *
 * As seções têm âncoras (`#orcamento`, `#conectores`, …): é para elas que o campo
 * travado do agente aponta, e a página rola até a seção depois de carregar.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, CheckCircle2, Loader2, Lock, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { errosPorCampo, podeEditarAtendimento, type ErroDeCampo } from '@/lib/atendimento'
import { atendimentoService, blueprintService } from '@/services/painel'
import type { Atendimento, BlueprintVersao, RecusaDaOrganizacao } from '@/schemas/painel'
import { useAuthStore } from '@/stores/auth-store'
import { useOrganizationStore } from '@/stores/organization-store'

type No = Record<string, unknown>

const SECOES = [
  ['atendimentos', 'Atendimentos'],
  ['orcamento', 'Orçamento'],
  ['conectores', 'Conectores'],
  ['operadores', 'Operadores'],
  ['retencao', 'Retenção'],
  ['registro', 'Registro de atendimento'],
] as const

export function AtendimentoDaOrganizacao() {
  const { currentOrganization } = useOrganizationStore()
  const { user } = useAuthStore()
  const organizationId = currentOrganization?.id ?? null
  const nome = currentOrganization?.name ?? 'organização'
  const podeEditar = podeEditarAtendimento(currentOrganization, user)

  const [agentes, setAgentes] = useState<Atendimento[]>([])
  const [versoes, setVersoes] = useState<BlueprintVersao[]>([])
  const [doc, setDoc] = useState<No | null>(null)
  const [escopo, setEscopo] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erros, setErros] = useState<ErroDeCampo[]>([])
  const [conferencia, setConferencia] = useState<BlueprintVersao['checked']>(null)
  const [recusa, setRecusa] = useState<RecusaDaOrganizacao | null>(null)

  const buscar = useCallback(async () => {
    if (!organizationId) return
    setCarregando(true)
    try {
      const [lista, asVersoes] = await Promise.all([
        atendimentoService.agentes(organizationId).catch(() => []),
        blueprintService.versoes(organizationId),
      ])
      setAgentes(lista)
      setVersoes(asVersoes)
      const maisRecente = asVersoes[0]
      if (maisRecente) {
        const corpo = await blueprintService.versao(organizationId, maisRecente.version)
        setDoc((corpo.spec ?? corpo) as No)
        setEscopo(String(corpo.scope ?? maisRecente.scope ?? 'legacy'))
      } else {
        setDoc(null)
        setEscopo(null)
      }
    } catch {
      toast.error('Não foi possível ler a configuração da organização.')
    } finally {
      setCarregando(false)
    }
  }, [organizationId])

  useEffect(() => {
    void buscar()
  }, [buscar])

  // A ÂNCORA: o link do campo travado chega aqui com `#secao`; rola depois de carregar.
  useEffect(() => {
    if (carregando) return
    const alvo = window.location.hash.replace('#', '')
    if (!alvo) return
    const el = document.getElementById(alvo)
    el?.scrollIntoView({ block: 'start' })
    el?.focus?.()
  }, [carregando])

  const vigente = versoes.find((v) => v.status === 'published')
  const rascunho = versoes.find((v) => v.status === 'draft')
  const { porCampo } = errosPorCampo(erros)
  const temErro = erros.length > 0

  const mudar = (caminho: string, valor: unknown) => {
    if (!doc) return
    setDoc({ ...doc, [caminho]: valor })
    setErros((lista) => lista.filter((e) => e.loc[0] !== caminho))
  }

  const salvarRascunho = async () => {
    if (!organizationId || !doc) return
    setSalvando(true)
    setRecusa(null)
    try {
      const novo = await blueprintService.criarRascunho(organizationId, doc)
      toast.success(`Rascunho v${novo.version} salvo.`)
      await buscar()
    } catch (erro) {
      const resposta = (erro as { response?: { status?: number; data?: { detail?: unknown } } }).response
      if (resposta?.status === 422) {
        setErros(errosPorCampo(resposta.data?.detail).lista)
        toast.error('Há campo que não é da organização.')
      } else if (resposta?.status === 403) {
        toast.error('O servidor recusou: campo do operador da plataforma alterado.')
      } else {
        toast.error('Não foi possível salvar o rascunho.')
      }
    } finally {
      setSalvando(false)
    }
  }

  const validar = async () => {
    if (!organizationId || !rascunho) return
    setSalvando(true)
    try {
      const resultado = await blueprintService.validar(organizationId, rascunho.version)
      setErros(resultado.valido ? [] : errosPorCampo(resultado.detail).lista)
      toast[resultado.valido ? 'success' : 'error'](resultado.valido ? 'Configuração válida.' : 'A configuração tem erros.')
    } finally {
      setSalvando(false)
    }
  }

  const publicar = async () => {
    if (!organizationId || !rascunho) return
    setSalvando(true)
    setRecusa(null)
    setConferencia(null)
    try {
      const publicado = await blueprintService.publicar(organizationId, rascunho.version)
      setConferencia(publicado.checked ?? [])
      toast.success(`v${rascunho.version} publicada para toda a ${nome}.`)
      await buscar()
    } catch (erro) {
      const resposta = (erro as { response?: { status?: number; data?: { detail?: unknown } } }).response
      const detalhe = resposta?.data?.detail
      if (resposta?.status === 409 && detalhe && typeof detalhe === 'object' && 'agents' in detalhe) {
        setRecusa(detalhe as RecusaDaOrganizacao)
        toast.error('Recusado: algum atendimento ficaria inválido.')
      } else {
        setErros(errosPorCampo(detalhe ?? 'publicação recusada').lista)
        toast.error('A publicação foi recusada.')
      }
    } finally {
      setSalvando(false)
    }
  }

  if (!organizationId) {
    return <p className="py-10 text-center text-gray-400">Escolha uma organização.</p>
  }
  if (carregando) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> lendo a configuração…
      </div>
    )
  }

  const legacy = escopo === 'legacy'
  const operadores = (doc?.operadores as No[] | undefined) ?? []
  const conectores = (doc?.conectores as No[] | undefined) ?? []
  const retencao = (doc?.retencao as No | undefined) ?? {}
  const registro = (doc?.registro_de_atendimento as No | null | undefined) ?? null
  const usoPorConector = new Map<string, string[]>()

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-100">
            <Building2 className="h-5 w-5 text-indigo-300" /> Atendimento da {nome}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-400">
            O que vale para todos os atendimentos da {nome}. Cada atendimento tem o próprio
            documento, no passo Atendimento do agente; aqui fica o que eles dividem.
          </p>
          <p className="mt-1 text-sm text-gray-400">
            {vigente ? `No ar · v${vigente.version}` : 'Nenhuma versão publicada'}
            {rascunho && ` · rascunho v${rascunho.version}`}
          </p>
        </div>
        {podeEditar && doc && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={salvando} onClick={() => void salvarRascunho()}>
              Salvar rascunho
            </Button>
            <Button variant="outline" size="sm" disabled={salvando || !rascunho} onClick={() => void validar()}>
              Validar
            </Button>
            <Button size="sm" disabled={salvando || !rascunho || temErro} onClick={() => void publicar()}>
              Publicar{rascunho ? ` v${rascunho.version}` : ''}
            </Button>
          </div>
        )}
      </div>

      {!podeEditar && (
        <div role="status" className="flex items-start gap-3 rounded-xl border border-gray-700/60 bg-white/5 p-4 text-sm text-gray-300">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
          <p>
            Você está vendo a configuração em leitura: só administradores da {nome} alteram e
            publicam. Peça a um deles.
          </p>
        </div>
      )}

      {legacy && (
        <div role="status" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
          Esta organização ainda está em documento único: a divisão por atendimento é feita pelo
          operador da plataforma. Até lá, o documento inteiro é editado no passo Atendimento do
          agente que atende.
        </div>
      )}

      {erros.length > 0 && (
        <div role="alert" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
          <p className="font-medium">A configuração tem {erros.length === 1 ? '1 erro' : `${erros.length} erros`}.</p>
          <ul className="mt-1 list-inside list-disc text-xs">
            {erros.map((e, i) => (
              <li key={i}>
                {e.loc.length > 0 && <span className="font-mono">{e.loc.join('.')}: </span>}
                {e.msg}
              </li>
            ))}
          </ul>
        </div>
      )}

      {recusa && (
        <div role="alert" className="space-y-2 rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-100">
          <p className="font-medium">
            Publicar foi recusado: {recusa.agents.length === 1 ? '1 atendimento ficaria' : `${recusa.agents.length} atendimentos ficariam`} inválido
            {recusa.agents.length === 1 ? '' : 's'}. Nada mudou.
          </p>
          <ul className="space-y-1">
            {recusa.agents.map((a) => (
              <li key={a.agent_id} className="flex items-start gap-2">
                <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-300" />
                <div>
                  <p className="font-medium">{a.agent_name ?? a.agent_id}</p>
                  <ul className="list-inside list-disc text-xs text-red-200/80">
                    {a.errors.map((e, i) => (
                      <li key={i}>
                        <span className="font-mono">{e.loc.join('.')}</span>: {e.msg}
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {conferencia && conferencia.length > 0 && (
        <div role="status" className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-100">
          <p className="font-medium">Publicado. Cada atendimento foi conferido:</p>
          <ul className="mt-1 flex flex-wrap gap-2 text-xs">
            {conferencia.map((c) => (
              <li key={c.agent_id} className="flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5">
                <CheckCircle2 className="h-3 w-3" /> {c.agent_name ?? c.agent_id}
              </li>
            ))}
          </ul>
        </div>
      )}

      <nav aria-label="Nesta página" className="flex flex-wrap gap-2 text-sm">
        {SECOES.map(([id, titulo]) => (
          <a key={id} href={`#${id}`} className="rounded-md border border-gray-700/60 px-2.5 py-1 text-gray-300 hover:bg-white/5">
            {titulo}
          </a>
        ))}
      </nav>

      <Secao id="atendimentos" titulo="Atendimentos desta organização">
        {agentes.length === 0 ? (
          <p className="text-sm text-gray-400">
            Nenhum atendimento ainda. Um atendimento nasce quando um agente é ligado a uma caixa
            de entrada, no editor do agente.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Agente</th>
                  <th className="py-2 pr-4 font-medium">Projeto</th>
                  <th className="py-2 pr-4 font-medium">Caixa de entrada</th>
                  <th className="py-2 pr-4 font-medium">Documento</th>
                  <th className="py-2 font-medium">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/60">
                {agentes.map((a) => (
                  <tr key={a.agent_id}>
                    <td className="py-2 pr-4 text-gray-100">{a.name}</td>
                    <td className="py-2 pr-4 text-gray-300">{a.project_name}</td>
                    <td className="py-2 pr-4 text-gray-300">
                      {a.inboxes.length === 0
                        ? 'sem caixa'
                        : a.inboxes.map((c) => (c.inbox_id === null ? 'qualquer caixa' : `caixa ${c.inbox_id}`)).join(', ')}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={cn('rounded px-2 py-0.5 text-xs', a.published_version ? 'bg-emerald-500/15 text-emerald-200' : 'bg-amber-500/15 text-amber-200')}>
                        {a.published_version ? `no ar · v${a.published_version}` : 'sem documento no ar'}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      <Link to={`/agents/${a.agent_id}/edit?step=7`} className="text-indigo-300 underline underline-offset-2">
                        Abrir atendimento
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-gray-500">
          Atendimento sem documento no ar não responde com IA: toda conversa dele vira transferência
          para a equipe.
        </p>
      </Secao>

      <Secao id="orcamento" titulo="Orçamento">
        <p className="text-sm text-gray-400">
          Um teto para a organização inteira. A mensagem que o cliente lê quando ele acaba é de cada
          atendimento.
        </p>
        <div className="space-y-1">
          <span className="text-sm font-medium text-gray-300">Ao atingir o teto</span>
          <p className="text-sm text-gray-100">
            Cada atendimento manda o próprio aviso e transfere para a Equipe de triagem dele.
          </p>
          <p className="text-xs text-gray-500">Ninguém fica sem resposta: a IA para, a equipe assume.</p>
        </div>
        <p className="text-xs text-gray-500">
          O teto diário é operação, ajustado pelo operador da plataforma; ele não mora no documento.
        </p>
      </Secao>

      <Secao id="conectores" titulo="Conectores" operador>
        <p className="text-sm text-gray-400">
          Os sistemas que os atendimentos consultam e onde agem. A credencial fica no cofre da
          organização e nunca aparece no documento do atendimento.
        </p>
        {conectores.length === 0 ? (
          <p className="text-sm text-gray-300">Nenhum conector declarado.</p>
        ) : (
          <ul className="divide-y divide-gray-800/60">
            {conectores.map((c) => (
              <li key={String(c.key)} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs text-gray-100">{String(c.key)}</span>
                <span className="text-xs uppercase text-gray-400">{String(c.tipo)}</span>
                {c.auth_ref ? <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs text-emerald-200">credencial no cofre</span> : <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-200">sem credencial</span>}
                {c.endpoint ? <span className="font-mono text-xs text-gray-500">{String(c.endpoint)}</span> : null}
                {(usoPorConector.get(String(c.key)) ?? []).length > 0 && <span className="text-xs text-gray-500">usado por {usoPorConector.get(String(c.key))!.join(', ')}</span>}
              </li>
            ))}
          </ul>
        )}
      </Secao>

      <Secao id="operadores" titulo="Operadores">
        <p className="text-sm text-gray-400">
          Pessoas da equipe que falam com o assistente em nome de um cliente. Valem em todos os
          atendimentos da organização.
        </p>
        {operadores.length === 0 && <p className="text-sm text-gray-300">Nenhum operador cadastrado.</p>}
        <ul className="space-y-2">
          {operadores.map((o, i) => (
            <li key={i} className="grid gap-2 sm:grid-cols-[8rem_1fr_1fr_auto_auto] sm:items-end">
              <div className="space-y-1">
                <Label htmlFor={`op-canal-${i}`} className="text-xs text-gray-400">
                  Canal
                </Label>
                <select id={`op-canal-${i}`} className="flex h-10 w-full rounded-md border border-gray-700/60 bg-white/5 px-3 text-sm text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 disabled:opacity-60" disabled={!podeEditar} value={String(o.canal ?? 'whatsapp')} onChange={(e) => mudar('operadores', operadores.map((x, k) => (k === i ? { ...x, canal: e.target.value } : x)))}>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="telegram">Telegram</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`op-id-${i}`} className="text-xs text-gray-400">
                  Telefone ou usuário
                </Label>
                <Input id={`op-id-${i}`} disabled={!podeEditar} aria-invalid={!!porCampo[`operadores.${i}.identificador`]} value={String(o.identificador ?? '')} onChange={(e) => mudar('operadores', operadores.map((x, k) => (k === i ? { ...x, identificador: e.target.value } : x)))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`op-nome-${i}`} className="text-xs text-gray-400">
                  Nome
                </Label>
                <Input id={`op-nome-${i}`} disabled={!podeEditar} value={String(o.nome ?? '')} onChange={(e) => mudar('operadores', operadores.map((x, k) => (k === i ? { ...x, nome: e.target.value || null } : x)))} />
              </div>
              <label className="flex items-center gap-2 pb-2 text-sm text-gray-300">
                <input type="checkbox" className="h-4 w-4 accent-indigo-500" disabled={!podeEditar} checked={o.ativo !== false} onChange={(e) => mudar('operadores', operadores.map((x, k) => (k === i ? { ...x, ativo: e.target.checked } : x)))} />
                ativo
              </label>
              {podeEditar && (
                <Button type="button" variant="outline" size="sm" aria-label={`Remover operador ${i + 1}`} onClick={() => mudar('operadores', operadores.filter((_, k) => k !== i))}>
                  Remover
                </Button>
              )}
            </li>
          ))}
        </ul>
        {podeEditar && (
          <Button type="button" variant="outline" size="sm" onClick={() => mudar('operadores', [...operadores, { canal: 'whatsapp', identificador: '', nome: null, ativo: true }])}>
            Adicionar operador
          </Button>
        )}
      </Secao>

      <Secao id="retencao" titulo="Retenção" operador>
        <p className="text-sm text-gray-100">
          Guardar as mensagens do canal por {String(retencao.channel_messages_dias ?? 60)} dias
          {retencao.anonimizar_context_apos_encerrar === false ? '' : ' · contexto anonimizado ao encerrar a conversa'}
        </p>
      </Secao>

      <Secao id="registro" titulo="Registro de atendimento" operador>
        <p className="text-sm text-gray-100">
          {registro ? `Enviar o registro de cada atendimento para ${String(registro.conector)}` : 'Não envia'}
        </p>
      </Secao>
    </div>
  )
}

function Secao({
  id,
  titulo,
  operador = false,
  children,
}: {
  id: string
  titulo: string
  operador?: boolean
  children: React.ReactNode
}) {
  return (
    <section id={id} tabIndex={-1} aria-labelledby={`h-${id}`} className="scroll-mt-24 space-y-3 rounded-xl border border-gray-700/50 p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60">
      <div className="flex items-center justify-between gap-3">
        <h3 id={`h-${id}`} className="text-base font-semibold text-gray-100">
          {titulo}
        </h3>
        {operador && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Lock className="h-3 w-3" /> operador da plataforma
          </span>
        )}
      </div>
      {children}
    </section>
  )
}
